import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Message } from '@/lib/api';
import LinkPreview from './LinkPreview';
import MentionAutocomplete from './MentionAutocomplete';
import { extractUrls } from '@/lib/links';
import { shortcodeToEmoji } from '@/lib/emoji-shortcodes';
import { useChatStore } from '@/stores/chat';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (text: string, replyToId?: string) => void;
  onTyping: (isTyping: boolean) => void;
  replyingTo: Message | null;
  onCancelReply: () => void;
  onCreatePoll?: () => void;
  /**
   * The conversation id — required for draft persistence (F-010). When
   * undefined the composer behaves as before (no drafts, no autocomplete).
   */
  conversationId?: string;
  maxLength?: number;
  disabled?: boolean;
}

const MAX = 4000;

export default function Composer({
  value,
  onChange,
  onSubmit,
  onTyping,
  replyingTo,
  onCancelReply,
  onCreatePoll,
  conversationId,
  maxLength = MAX,
  disabled,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const idleTimer = useRef<number | null>(null);
  const [showPollHint, setShowPollHint] = useState(false);
  // MentionAutocomplete is open whenever there's a non-empty `@query` token
  // immediately preceding the caret. We compute it on every keystroke and
  // re-derive after a mention is inserted (the parent's `value` flows back
  // through `handleChange` and we re-evaluate).
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  // F-010: drafts live in the chat store. We don't subscribe to the slice
  // here because hydration only needs to happen on conversation switch —
  // re-rendering on every keystroke would fight the parent's controlled
  // `value` prop. The `setDraft` / `clearDraft` actions keep the slice in
  // sync as the user types / sends.
  const setDraft = useChatStore((s) => s.setDraft);
  const clearDraft = useChatStore((s) => s.clearDraft);
  const hydratedFor = useRef<string | null>(null);

  const detectedUrl = useMemo(() => {
    const urls = extractUrls(value);
    return urls[0];
  }, [value]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [value]);

  // F-010: hydrate from saved draft when the conversation changes. We only
  // do this once per conversationId so a manual clear / submit doesn't
  // re-populate the field. The parent's `onChange` is what writes edits
  // back through `setDraft`.
  useEffect(() => {
    if (!conversationId) {
      hydratedFor.current = null;
      return;
    }
    if (hydratedFor.current === conversationId) return;
    hydratedFor.current = conversationId;
    const saved = useChatStore.getState().draftsByConversation[conversationId];
    if (saved && saved.content) {
      onChange(saved.content);
    } else {
      // No saved draft for this conversation — clear the visible value so
      // the textarea doesn't keep the previous conversation's text. Without
      // this, switching to a fresh conversation would show leftover text
      // that the user thinks they're typing into the new one (and the
      // store would write it back as a draft for the NEW conversation).
      onChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Typing: send true on first input, then debounce false after 3s
  const scheduleStop = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => onTyping(false), 3000);
  }, [onTyping]);

  // Flush `isTyping:false` when the composer unmounts (e.g. user switches
  // to another conversation, or the modal closes). Without this, the
  // idleTimer is silently dropped and the recipient keeps seeing
  // "<name> is typing…" for up to 3 seconds — or until the next keystroke.
  useEffect(() => {
    return () => {
      if (idleTimer.current) {
        window.clearTimeout(idleTimer.current);
        idleTimer.current = null;
      }
      onTyping(false);
    };
    // onTyping identity changes per-conversation; we only want to flush
    // on UNMOUNT, not on every prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  /**
   * Walk backwards from `caret` looking for an `@<word>` token with no
   * whitespace between it and the caret. Returns the substring after `@`
   * (the query), or null when no mention is being typed.
   */
  function currentMentionQuery(text: string, caret: number): string | null {
    if (caret <= 0 || caret > text.length) return null;
    // Find the last `@` before the caret, then make sure everything between
    // it and the caret is a valid username character. If there's a space,
    // the mention has already "closed" and we shouldn't show the popover.
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === '@') {
        const after = text.slice(i + 1, caret);
        // Username chars are word chars plus a few extras (`.`, `-`, `_`).
        if (/^[\w.\-]*$/.test(after)) return after;
        return null;
      }
      if (/\s/.test(ch)) return null;
      i -= 1;
    }
    return null;
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value;
    if (next.length <= maxLength) {
      onChange(next);
      onTyping(true);
      scheduleStop();
      const caret = e.target.selectionStart ?? next.length;
      setMentionQuery(currentMentionQuery(next, caret));
      // F-010: persist the in-progress draft per-conversation. We deliberately
      // write the literal text (no shortcode expansion) so the user sees what
      // they typed when they come back to the conversation.
      if (conversationId) {
        setDraft(conversationId, next, replyingTo?.id ?? null);
      }
    }
  }

  function insertMention(username: string) {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? value.length;
    // Walk back to the `@` and replace `@<query>` with `@<username>` (plus a
    // trailing space so the user can keep typing immediately).
    let i = caret - 1;
    while (i >= 0 && value[i] !== '@' && !/\s/.test(value[i])) i -= 1;
    const start = i >= 0 && value[i] === '@' ? i : caret;
    const next = `${value.slice(0, start)}@${username} ${value.slice(caret)}`;
    onChange(next);
    if (conversationId) {
      setDraft(conversationId, next, replyingTo?.id ?? null);
    }
    setMentionQuery(null);
    // Restore focus + caret after the inserted mention.
    requestAnimationFrame(() => {
      ta.focus();
      const newCaret = start + 1 + username.length + 1;
      ta.setSelectionRange(newCaret, newCaret);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // When the mention popover is open and the user hits Enter, we let
    // MentionAutocomplete's own onKeyDown handler take over (it calls
    // preventDefault and picks a suggestion). Otherwise Enter submits.
    // Arrow keys are also consumed by the popover.
    if (mentionQuery !== null && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  }

  function submit() {
    const trimmedRaw = value.trim();
    if (!trimmedRaw || disabled) return;
    // F-012: convert `:shortcodes:` to emoji at submit time. The composer
    // shows the raw text while typing so users can edit shortcodes they
    // remember; the renderer / Markdown component also runs the conversion
    // a second time on display, so even if a draft is restored verbatim
    // the message bubbles still show the unicode glyph.
    const trimmed = shortcodeToEmoji(trimmedRaw);
    onSubmit(trimmed, replyingTo?.id);
    onChange('');
    setMentionQuery(null);
    onTyping(false);
    if (conversationId) {
      clearDraft(conversationId);
    }
    if (idleTimer.current) {
      window.clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }

  const ratio = value.length / maxLength;
  const warn = ratio >= 0.9;
  const blocked = value.length >= maxLength;

  return (
    <div className="border-t border-ink-100 bg-white px-3 pb-safe pt-2 dark:border-ink-700 dark:bg-ink-800">
      {replyingTo && (
        <div className="mb-2 flex items-center gap-2 rounded-md bg-ink-100 px-3 py-2 text-xs dark:bg-ink-700">
          <span className="flex-1 truncate">
            Replying to <span className="font-medium">{replyingTo.sender?.displayName ?? 'message'}</span>: {replyingTo.content}
          </span>
          <button type="button" aria-label="Cancel reply" className="btn-ghost px-1 py-0.5" onClick={onCancelReply}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {detectedUrl && !replyingTo && (
        <LinkPreview url={detectedUrl} className="mb-2 max-w-md" />
      )}

      {/* Mention autocomplete (F-012). Rendered above the textarea in the
          normal flow so it doesn't fight with the iOS keyboard / virtual
          keyboard insets. Hidden when there's no current mention. The
          MentionAutocomplete component does its own debounced server-side
          search via Users.search, so we don't pass candidates here. */}
      {mentionQuery !== null && (
        <MentionAutocomplete
          query={mentionQuery}
          onSelect={insertMention}
          onDismiss={() => setMentionQuery(null)}
        />
      )}

      <div className="flex items-end gap-2">
        <textarea
          ref={taRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Connecting…' : 'Write a message'}
          rows={1}
          className="nice-scroll max-h-60 min-h-[40px] flex-1 resize-none rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-ink-700 dark:bg-ink-900"
          aria-label="Message"
          disabled={disabled}
        />
        {onCreatePoll && (
          <button
            type="button"
            aria-label="Create poll"
            title="Create poll"
            className="btn-ghost px-2"
            onClick={onCreatePoll}
            disabled={disabled}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 21V3M3 12h18M9 6h12M9 18h12" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="btn-primary px-3"
          onClick={submit}
          disabled={!value.trim() || blocked || disabled}
          aria-label="Send message"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
          </svg>
        </button>
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-ink-500 dark:text-ink-300">
        <button
          type="button"
          className="hover:underline"
          onMouseEnter={() => setShowPollHint(true)}
          onMouseLeave={() => setShowPollHint(false)}
          onFocus={() => setShowPollHint(true)}
          onBlur={() => setShowPollHint(false)}
          aria-label="Markdown supported"
        >
          Markdown supported
        </button>
        {showPollHint && (
          <span className="hidden">Use **bold**, *italic*, `code`, &gt; quote, - lists, [links](https://…).</span>
        )}
        <span
          className={clsx(
            warn ? (blocked ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400') : '',
          )}
          aria-live="polite"
        >
          {value.length}/{maxLength}
        </span>
      </div>
    </div>
  );
}